/**
 * Events → Organizers ("רכזי שטח") — A1 scorecard module.
 *
 * Composes the live `/v1/contract/organizers` envelope with the
 * honesty primitives and the `<Scorecard/>` leaderboard chart.
 *
 * Data-quality rule #5 (CRITICAL): organizers with `events <
 * MIN_EVENTS` (default 4) come back with `insufficient_data: true`.
 * They are EXCLUDED from the ranked leaderboard and shown in a
 * separate "מתחת לסף" panel with no rank, each tagged with an
 * `<InsufficientData/>` honesty chip.
 *
 * `yes_show` is DERIVED here: `(attended - walk_in) / rsvp_yes`.
 */
import type { Suite, ModuleSpec } from '../../../data/types/platform';
import { useEventsOrganizers } from '../../../data/queries/useEventsOrganizers';
import type { OrganizerRow } from '../../../data/api/events';
import { Scorecard, type ScorecardRow } from '../../../components/charts/Scorecard';
import {
  AnomalyChip,
  Denominator,
  Freshness,
  InsufficientData,
} from '../../../components/honesty';
import { Icon } from '../../../components/ui/Icon';
import { formatNumber, formatPercent } from '../../../utils/format';

/** Per the brief: organizers with events < MIN_EVENTS are not ranked. */
const MIN_EVENTS = 4;

export interface OrganizersViewProps {
  system?: Suite;
  module?: ModuleSpec;
}

function toEpoch(iso: string): number {
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : Date.now();
}

/**
 * Live data currently returns Airtable record ids in `he`
 * (e.g. `rechkuSE623b8rOz0`). Detect them so the view can degrade
 * gracefully — show the id, but flag it as a record-id fallback so
 * the user understands they're not looking at a real name.
 */
function looksLikeAirtableId(s: string): boolean {
  return /^rec[A-Za-z0-9]{14,}$/.test(s);
}

function formatMixHe(mix?: string): string | undefined {
  if (mix === 'rally-heavy') return 'עצרות המונים';
  if (mix === 'parlor-heavy') return 'מפגשי בית';
  if (mix === 'mixed') return 'מעורב';
  return undefined;
}

function yesShowOf(o: OrganizerRow): number {
  const yesAtt = Math.max(0, o.attended - o.walk_in);
  return o.rsvp_yes > 0 ? yesAtt / o.rsvp_yes : 0;
}

export function OrganizersView({ system, module }: OrganizersViewProps) {
  const { data, isLoading, error } = useEventsOrganizers();

  return (
    <article className="module-view organizers-view module-page">
      <header className="mp-head">
        <div className="mp-eyebrow">
          <span>{system?.en ?? 'EVENTS'}</span>
          <span className="mp-sep">/</span>
          <span>{module?.he ?? 'רכזי שטח'}</span>
        </div>
        <h1>{module?.he ?? 'רכזי שטח'}</h1>
        <p className="mp-sub">
          {module?.sub ??
            `רק רכז/ות עם ${MIN_EVENTS}+ אירועים מדורגים. מתחת לסף — מסומן כנתונים לא מספיקים.`}
        </p>
        <div className="mp-meta">
          {data && (
            <>
              <Freshness updatedAt={toEpoch(data.source_pulled_at)} />
              <span className="mp-dot" />
              <span>scope: {data.scope}</span>
              <span className="mp-dot" />
              <span>סף דירוג: {MIN_EVENTS}+ אירועים</span>
              {data.meta.mock && (
                <>
                  <span className="mp-dot" />
                  <span>mock fixtures</span>
                </>
              )}
            </>
          )}
        </div>
      </header>

      <div className="mp-body">
        {isLoading && (
          <div className="mp-panel">
            <div className="mp-panel-body">
              <div className="mp-placeholder">
                <Icon name="loader" size={20} />
                <span>טוען רכזי שטח…</span>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="mp-panel">
            <div className="mp-panel-body">
              <div className="mp-placeholder">
                <Icon name="alert-triangle" size={20} />
                <span>שגיאה בטעינת המודול: {error.message}</span>
              </div>
            </div>
          </div>
        )}

        {data && <OrganizersBody envelope={data} />}
      </div>
    </article>
  );
}

interface OrganizersBodyProps {
  envelope: NonNullable<ReturnType<typeof useEventsOrganizers>['data']>;
}

function OrganizersBody({ envelope }: OrganizersBodyProps) {
  const rows = envelope.data ?? [];

  // Partition by data-quality rule #5.
  const ranked = rows
    .filter((o) => !o.insufficient_data && o.events >= MIN_EVENTS)
    .sort((a, b) => b.attended - a.attended);
  const subThreshold = rows
    .filter((o) => o.insufficient_data || o.events < MIN_EVENTS)
    .sort((a, b) => b.events - a.events);

  // Derived KPI: average yes_show over RANKED organizers only.
  const avgYesShow =
    ranked.length > 0
      ? ranked.reduce((s, o) => s + yesShowOf(o), 0) / ranked.length
      : 0;

  // Count of rows where `he` is actually a record-id fallback. Surfaced
  // in the footer as an honesty signal.
  const idFallbacks = rows.filter((o) => looksLikeAirtableId(o.he)).length;

  // Bar-scaling denominator: max attended across the ranked set so the
  // subthreshold rows don't squash the leaderboard.
  const maxAttended = ranked.reduce((m, o) => Math.max(m, o.attended), 0);

  const toScorecardRow = (o: OrganizerRow, opts?: { muted?: boolean }): ScorecardRow => {
    const isId = looksLikeAirtableId(o.he);
    const labelBits = [
      o.district || '—',
      formatMixHe(o.format_mix),
      isId ? '(record id)' : null,
    ].filter(Boolean) as string[];
    return {
      id: o.he,
      label: o.he,
      sub: labelBits.join(' · '),
      primary: o.attended,
      secondary: o.events,
      rate: yesShowOf(o),
      insufficient: opts?.muted || o.insufficient_data || o.events < MIN_EVENTS,
    };
  };

  const rankedRows: ScorecardRow[] = ranked.map((o) => toScorecardRow(o));
  const subRows: ScorecardRow[] = subThreshold.map((o) =>
    toScorecardRow(o, { muted: true }),
  );

  return (
    <>
      {/* KPI strip — total / ranked / below-threshold / avg yes_show. */}
      <div className="kpi-grid cols-4">
        <KpiCell
          label="רכזי שטח פעילים"
          value={formatNumber(rows.length)}
          sub="סך-הכל בנתונים"
        />
        <KpiCell
          label={`עברו את הסף (${MIN_EVENTS}+ אירועים)`}
          value={formatNumber(ranked.length)}
          sub={`מתוך ${formatNumber(rows.length)} רכזים`}
          accent
        />
        <KpiCell
          label="מתחת לסף"
          value={formatNumber(subThreshold.length)}
          sub="לא מדורגים — נתונים לא מספיקים"
        />
        <KpiCell
          label="ממוצע אישור←הגעה"
          value={ranked.length > 0 ? `${Math.round(avgYesShow * 100)}%` : '—'}
          sub={
            ranked.length > 0
              ? `ממוצע משוקלל לפי רכז · n=${ranked.length}`
              : 'אין מספיק נתונים'
          }
          focus
        />
      </div>

      {/* Main panel — ranked leaderboard (insufficient rows excluded). */}
      <div className="panel">
        <div className="panel-head">
          <div className="title">דירוג רכזי שטח</div>
          <div className="sub">
            מיון לפי הגעות בפועל · מציג רק רכזים עם {MIN_EVENTS}+ אירועים ·{' '}
            <b>
              דורגו {formatNumber(ranked.length)} מתוך {formatNumber(rows.length)}
            </b>
          </div>
        </div>
        {rankedRows.length > 0 ? (
          <Scorecard
            rows={rankedRows}
            maxPrimary={maxAttended}
            columns={{
              rank: '',
              label: 'רכז · מחוז',
              bar: 'תרשים השוואה',
              primary: 'הגיעו',
              rate: 'המרת אישור←הגעה',
              secondary: 'אירועים',
            }}
          />
        ) : (
          <div className="mp-panel-body">
            <div className="mp-placeholder">
              <Icon name="alert-triangle" size={16} />
              <span>אין רכזים שעוברים את סף הדירוג כרגע.</span>
            </div>
          </div>
        )}
      </div>

      {/* Secondary panel — below-threshold organizers. No rank. */}
      {subRows.length > 0 && (
        <div className="panel">
          <div className="panel-head">
            <div className="title">מתחת לסף — אין דירוג</div>
            <div className="sub">
              פחות מ-{MIN_EVENTS} אירועים — שיעור ההמרה לא מהימן סטטיסטית.
              מוצג למידע בלבד.
            </div>
          </div>
          <Scorecard
            rows={subRows}
            showRank={false}
            columns={{
              label: 'רכז · מחוז',
              bar: 'אין דירוג',
              primary: 'הגיעו',
              rate: 'המרת אישור←הגעה',
              secondary: 'אירועים',
            }}
            renderTrailing={(row) => (
              <InsufficientData
                threshold={MIN_EVENTS}
                actual={Math.max(0, Math.round(row.secondary ?? 0))}
              />
            )}
          />
        </div>
      )}

      {/* Honesty footer. */}
      <footer
        className="mv-foot"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 12,
          padding: '16px 0 4px',
          fontFamily: 'var(--font-sans)',
          fontSize: 12,
          color: 'var(--ink-3)',
          alignItems: 'center',
        }}
      >
        <Denominator
          n={ranked.length}
          total={rows.length}
          label={`רכזים מדורגים (סף ${MIN_EVENTS}+ אירועים)`}
        />
        <span style={{ color: 'var(--ink-3)' }}>
          רישום הגעה: {formatPercent(envelope.recorded.attendance, 0)}
        </span>
        <span style={{ color: 'var(--ink-3)' }}>
          רישום RSVP: {formatPercent(envelope.recorded.rsvp, 0)}
        </span>
        {idFallbacks > 0 && (
          <span
            style={{ color: 'var(--ink-3)' }}
            title="שם הרכז/ת לא נפתר ומוצג מזהה Airtable במקום"
          >
            {formatNumber(idFallbacks)} שמות לא נפתרו — מוצג record id
          </span>
        )}
        {envelope.meta.anomalies.map((a) => (
          <AnomalyChip
            key={a.key}
            severity={a.severity === 'critical' ? 'alert' : a.severity}
            reason={a.message}
          />
        ))}
      </footer>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* KpiCell — mirrors the funnel view's local helper.                  */
/* ------------------------------------------------------------------ */

interface KpiCellProps {
  label: string;
  value: string;
  sub?: string;
  accent?: boolean;
  focus?: boolean;
}

function KpiCell({ label, value, sub, accent, focus }: KpiCellProps) {
  const cls = ['kpi-cell', accent && 'accent', focus && 'focus']
    .filter(Boolean)
    .join(' ');
  return (
    <div className={cls}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
      {sub && <div className="kpi-meta">{sub}</div>}
    </div>
  );
}
